import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { formatUsdMoney } from '../../../utils/animatedNumber';

type CountValueProps = {
    value: number;
    isLoading: boolean;
};

type CountValueComparisonProps = CountValueProps & {
    comparisonValue: number;
    showComparison: boolean;
};

export const SubscriberCountValue = React.memo(({ value, isLoading }: CountValueProps) => {
    if (isLoading) {
        return <Skeleton width={40} sx={{ ml: 'auto' }} />;
    }

    return (
        <Typography textAlign="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {value.toLocaleString()}
        </Typography>
    );
});

SubscriberCountValue.displayName = 'SubscriberCountValue';

export const SubscriberCountValueWithComparison = React.memo(
    ({ value, comparisonValue, isLoading, showComparison }: CountValueComparisonProps) => {
        if (isLoading) {
            return <Skeleton width={40} sx={{ ml: 'auto' }} />;
        }
        const delta = value - comparisonValue;
        const isPositive = delta >= 0;
        return (
            <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.02rem', fontWeight: 600 }}>
                    {value.toLocaleString()}
                </Typography>
                {showComparison ? (
                    <Typography
                        variant="caption"
                        sx={{
                            color: isPositive ? 'success.main' : 'error.main',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {isPositive ? '↑' : '↓'} {Math.abs(delta).toLocaleString()}
                    </Typography>
                ) : null}
            </Box>
        );
    },
);

SubscriberCountValueWithComparison.displayName = 'SubscriberCountValueWithComparison';

export const SubscriberMetricValueWithComparison = React.memo(
    ({ value, comparisonValue, isLoading, showComparison }: CountValueComparisonProps) => {
        if (isLoading) {
            return <Skeleton width={72} sx={{ ml: 'auto' }} />;
        }

        const delta = value - comparisonValue;
        const isPositive = delta >= 0;

        return (
            <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.02rem', fontWeight: 600 }}>
                    {formatUsdMoney(value)}
                </Typography>
                {showComparison ? (
                    <Typography
                        variant="caption"
                        sx={{
                            color: isPositive ? 'success.main' : 'error.main',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {isPositive ? '↑' : '↓'} {formatUsdMoney(Math.abs(delta))}
                    </Typography>
                ) : null}
            </Box>
        );
    },
);

SubscriberMetricValueWithComparison.displayName = 'SubscriberMetricValueWithComparison';
